import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SOLUTIONS } from "../data/navigation";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const GUEST_COUNTS = ["Under 100", "100 – 500", "500 – 1,000", "1,000 – 5,000", "5,000+"];
const BUDGETS = ["Under ₹5 Lakh", "₹5 – 15 Lakh", "₹15 – 50 Lakh", "₹50 Lakh – ₹1 Crore", "₹1 Crore +"];

const EMPTY = { name: "", company: "", email: "", mobile: "", event_type: "", guest_count: "", location: "", event_date: "", budget: "", details: "" };

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#081D35] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#32B5E8]/50 focus:border-[#32B5E8] transition";

export const EnquiryForm = ({ dark = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Thank you! Your enquiry has been sent to the Evontra team.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="enquiry-form" onSubmit={submit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Name *" className={inputCls} />
        <input data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder="Company" className={inputCls} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
        <input data-testid="enquiry-mobile-input" required type="tel" value={form.mobile} onChange={set("mobile")} placeholder="Mobile *" className={inputCls} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <select data-testid="enquiry-event-type-select" required value={form.event_type} onChange={set("event_type")} className={inputCls}>
          <option value="" disabled>Event Type *</option>
          {SOLUTIONS.map((s) => (
            <optgroup key={s.id} label={s.title}>
              {s.items.map((item) => <option key={item} value={item}>{item}</option>)}
            </optgroup>
          ))}
          <option value="Other">Other</option>
        </select>
        <select data-testid="enquiry-guest-count-select" value={form.guest_count} onChange={set("guest_count")} className={inputCls}>
          <option value="" disabled>Guest Count</option>
          {GUEST_COUNTS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <select data-testid="enquiry-location-select" value={form.location} onChange={set("location")} className={inputCls}>
          <option value="" disabled>Location (State)</option>
          {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input data-testid="enquiry-event-date-input" type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} />
      </div>
      <select data-testid="enquiry-budget-select" value={form.budget} onChange={set("budget")} className={inputCls}>
        <option value="" disabled>Budget</option>
        {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
      </select>
      <textarea data-testid="enquiry-details-textarea" rows={4} value={form.details} onChange={set("details")} placeholder="Event Details — goals, audience, city, anything else we should know…" className={inputCls} />
      <button data-testid="enquiry-submit-button" type="submit" disabled={loading} className="ev-btn-primary w-full inline-flex items-center justify-center gap-2 text-white text-sm font-bold px-8 py-4 rounded-full disabled:opacity-60 disabled:pointer-events-none">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <>Submit Enquiry <ArrowRight size={16} /></>}
      </button>
    </form>
  );
};
